import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Crop, ReactCrop} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import styles from './UniversalImageUploadModal.module.scss';
import {ActionModal} from '../ActionModal';

interface UniversalImageUploadModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  file: File | null;
  previewUrl: string | null;
  onConfirm: (blob: Blob) => void;
  onCancel?: () => void;
  aspectRatio?: number;
  loading?: boolean;
}

export const UniversalImageUploadModal = ({
  open,
  setOpen,
  file,
  previewUrl: initialPreviewUrl,
  onConfirm,
  onCancel,
  aspectRatio = 16 / 9,
  loading = false,
}: UniversalImageUploadModalProps) => {
  const [crop, setCrop] = useState<Crop | null>(null);
  const [completedCrop, setCompletedCrop] = useState<Crop | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(initialPreviewUrl);

  useEffect(() => {
    if (!open) {
      setCrop(null);
      setCompletedCrop(null);
      setPreviewUrl(null);
      imgRef.current = null;
    } else {
      setPreviewUrl(initialPreviewUrl);
    }
  }, [open, initialPreviewUrl]);

  useEffect(() => {
    if (open && file && !previewUrl) {
      const reader = new FileReader();
      reader.onloadend = () => setPreviewUrl(reader.result as string);
      reader.readAsDataURL(file);
    }
  }, [open, file, previewUrl]);

  const onImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      imgRef.current = e.currentTarget;
      const {width, height} = e.currentTarget;

      const initialCropWidth = width > height * aspectRatio ? height * aspectRatio * 0.8 : width * 0.8;
      const initialCropHeight = initialCropWidth / aspectRatio;

      const initialCrop: Crop = {
        unit: 'px',
        width: initialCropWidth,
        height: initialCropHeight,
        x: (width - initialCropWidth) / 2,
        y: (height - initialCropHeight) / 2,
      };

      setCrop(initialCrop);
      setCompletedCrop(initialCrop);
    },
    [aspectRatio],
  );

  const handleConfirm = async () => {
    if (completedCrop && imgRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const image = imgRef.current;
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;

      canvas.width = completedCrop.width * scaleX;
      canvas.height = completedCrop.height * scaleY;
      const ctx = canvas.getContext('2d');

      if (ctx) {
        ctx.drawImage(
          image,
          completedCrop.x * scaleX,
          completedCrop.y * scaleY,
          completedCrop.width * scaleX,
          completedCrop.height * scaleY,
          0,
          0,
          canvas.width,
          canvas.height,
        );

        canvas.toBlob(
          (blob) => {
            if (blob) {
              onConfirm(blob);
            }
          },
          'image/jpeg',
          0.95,
        );
      }
    }
  };

  const handleClose = () => {
    setOpen(false);
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <ActionModal
      open={open}
      size="m"
      onClose={handleClose}
      setOpen={setOpen}
      submitForm={handleConfirm}
      caption="Обрежьте изображение"
      buttonsSize={'l'}
      textButtonApply="Сохранить"
      textButtonCancel="Отмена"
      loading={loading}>
      <div className={styles.modalContent}>
        {previewUrl && (
          <div className={styles.cropContainer}>
            <ReactCrop
              crop={crop || undefined}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(c: Crop) => setCompletedCrop(c)}
              aspect={aspectRatio}
              circularCrop={false}
              className={styles.cropper}
              disabled={loading}>
              <img
                ref={imgRef}
                src={previewUrl}
                onLoad={onImageLoad}
                alt="Предпросмотр"
                className={styles.previewImage}
                style={{opacity: loading ? 0.5 : 1}}
              />
            </ReactCrop>
          </div>
        )}
        <canvas ref={canvasRef} className={styles.hiddenCanvas} />
      </div>
    </ActionModal>
  );
};
